import {
  Alert,
  AlertIcon,
  Box,
  Center,
  Spinner,
  Text,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import BatchFileViewer from '../../components/pdf_viewer/BatchFileViewer';
import FileViewer from '../../components/pdf_viewer/FileViewer';
import { useAuditReportAttachment } from '../../components/pdf_viewer/AuditReportViewer';
import ROUTES from '../../constants/Routes';
import auditSheetRepository from '../../repositories/AuditSheetRepository';
import dashboardRepository from '../../repositories/DashboardRepository';

const DocumentViewerPage = () => {
  const { chartId, batchId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewerData, setViewerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reportChartId = viewerData?.upload_id;
  const isTocChart = typeof reportChartId === 'string' && reportChartId.startsWith('TOC');
  const { attachment } = useAuditReportAttachment({
    chartId: isTocChart ? reportChartId : null,
  });

  useEffect(() => {
    let isActive = true;

    const loadViewerData = async () => {
      setLoading(true);
      setError(null);

      try {
        const rendererId = searchParams.get('rendererId');
        const response = chartId
          ? await auditSheetRepository.getAuditUpload(chartId)
          : await dashboardRepository.getFileObjectByBatchId(
            batchId,
            rendererId ? { renderer_id: rendererId } : null
          );

        if (isActive) setViewerData(response);
      } catch (loadError) {
        if (isActive) {
          setError(loadError?.message || 'Unable to load documents');
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    if (chartId || batchId) {
      loadViewerData();
    } else {
      setError('A chart or batch identifier is required');
      setLoading(false);
    }

    return () => {
      isActive = false;
    };
  }, [batchId, chartId, searchParams]);

  const attachments = useMemo(() => [
    ...(viewerData?.attachments || []),
    ...(chartId && attachment ? [attachment] : []),
  ], [attachment, chartId, viewerData]);

  const handleClose = useCallback(() => {
    window.close();

    // window.close() is ignored when this route was opened directly rather
    // than by script. In that case, return to a safe application screen.
    window.setTimeout(() => {
      if (!window.closed) navigate(ROUTES.MY_CHARTS_V2 || ROUTES.DASHBOARD);
    }, 100);
  }, [navigate]);

  if (loading) {
    return (
      <Center minH="100vh" bg="gray.50" flexDirection="column" gap={3}>
        <Spinner size="lg" color="blue.500" />
        <Text color="gray.600">Loading documents...</Text>
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="100vh" bg="gray.50" p={6}>
        <Alert status="error" maxW="640px" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">Unable to open document viewer</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
        </Alert>
      </Center>
    );
  }

  if (chartId) {
    return (
      <FileViewer
        isOpen
        currentFile={viewerData?.file_obj}
        attachments={attachments}
        chartId={viewerData?.upload_id || viewerData?.file_id || chartId}
        onClose={handleClose}
      />
    );
  }

  return (
    <BatchFileViewer
      isOpen
      batchFiles={viewerData || []}
      onClose={handleClose}
    />
  );
};

export default DocumentViewerPage;
